import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Stepper, Select } from "@mantine/core";
import "../../../site.css";
import { useLanguage } from "../../../context/LanguageContext";
import HeroSectionV2 from "../../../components/HeroSectionV2";
import Billede1 from "../../../Images/Billlede-1.png";
import { useParams } from "react-router-dom";

const apiUrl = process.env.REACT_APP_API_URL as string;

interface FormMeetingDto {
    meetingId?: number;
    meetingDate?: string;
    meetingStart?: string;
    meetingSlut?: string;
    meetingName?: string;
    kontaktPerson?: string;
    kontaktPersonMobile?: string;
    kontaktPersonEmail?: string;
    antalPersoner?: number;
    bordOpstilling?: number;
    udstyr?: number[];
    meetingPakkeId?: number;
}

interface OptionDto {
    value: number;
    text: string;
}

const CreateMeeting: React.FC = () => {
    const { language } = useLanguage();

    const t = useMemo(() => {
        const da = language === "da";

        return {
            stepContact: da ? "Kontakt" : "Contact",
            stepBasic: da ? "Basic" : "Basic",
            stepSetup: da ? "Opsætning" : "Setup",
            stepConfirm: da ? "Bekræft" : "Confirm",

            heroTitleEn: "Book meeting",
            heroTitleDa: "Book møde",
            heroDescEn: "Here you can book a meeting room",
            heroDescDa: "Her kan du booke mødelokale",

            cardTitleCreate: da ? "Book mødelokale" : "Book meeting room",
            cardTitleEdit: da ? "Ret mødebooking" : "Edit meeting booking",

            lblMeetingName: da ? "Mødenavn" : "Meeting name",
            lblMeetingDate: da ? "Mødedato" : "Meeting date",
            lblMeetingStart: da ? "Mødestart" : "Meeting start",
            lblMeetingSlut: da ? "Mødeslut" : "Meeting end",
            lblPackage: da ? "Mødepakke" : "Meeting package",
            lblPersons: da ? "Antal personer" : "Number of persons",

            lblContactPerson: da ? "Kontaktperson" : "Contact person",
            lblMobile: da ? "Mobil" : "Mobile",
            lblEmail: da ? "Email" : "Email",

            lblTableSetup: da ? "Bordopstilling" : "Table setup",
            lblEquipment: da ? "Udstyr" : "Equipment",

            tableTitle: da ? "Mine mødebookinger" : "My meeting bookings",
            tableName: da ? "Mødenavn" : "Meeting name",
            tableDate: da ? "Dato" : "Date",
            tableStart: da ? "Start" : "Start",
            tableEnd: da ? "Slut" : "End",
            tablePackage: da ? "Mødepakke" : "Meeting package",
            tablePersons: da ? "Antal" : "Persons",
            tableAction: da ? "Handling" : "Action",
            edit: da ? "Ret" : "Edit",
            noBookings: da ? "Ingen mødebookinger fundet" : "No meeting bookings found",

            back: da ? "Tilbage" : "Back",
            next: da ? "Næste" : "Next",
            save: da ? "Gem" : "Save",
            create: da ? "Opret" : "Create",
            newBooking: da ? "Ny booking" : "New booking",

            savedCreate: da ? "Mødebooking gemt ✅" : "Meeting booking saved ✅",
            savedUpdate: da ? "Mødebooking opdateret ✅" : "Meeting booking updated ✅",
            saveError: da ? "Kunne ikke gemme mødebookingen." : "Could not save meeting booking.",
            loadError: da ? "Kunne ikke hente data." : "Could not load data.",
            invalidEmail: da ? "Indtast en gyldig email." : "Enter a valid email.",
            validationError: da ? "Udfyld venligst alle påkrævede felter." : "Please fill out all required fields.",

            dash: "—",
        };
    }, [language]);

    const [active, setActive] = useState(0);
    const [meetingPakker, setMeetingPakker] = useState<OptionDto[]>([]);
    const [meetingBordopstillinger, setMeetingBordopstillinger] = useState<OptionDto[]>([]);
    const [meetingUdstyrOptions, setMeetingUdstyrOptions] = useState<OptionDto[]>([]);

    const [bookings, setBookings] = useState<FormMeetingDto[]>([]);
    const [formData, setFormData] = useState<FormMeetingDto>({});
    const [editingId, setEditingId] = useState<number | null>(null);
    const [savedMessage, setSavedMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { eventId, userId } = useParams();

    const parsedEventId =
        eventId && !Number.isNaN(Number(eventId)) ? Number(eventId) : 0;

    const parsedUserId =
        userId && !Number.isNaN(Number(userId)) ? Number(userId) : 0;


    const isValidEmail = (email?: string): email is string =>
        !!email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    const handleApiResponse = async <T,>(res: Response): Promise<T> => {
        if (!res.ok) {
            const contentType = res.headers.get("content-type") || "";

            if (contentType.includes("application/json")) {
                const data = await res.json();
                throw new Error(
                    data?.message ||
                    data?.title ||
                    data?.detail ||
                    JSON.stringify(data)
                );
            }

            const text = await res.text();
            throw new Error(text || t.loadError);
        }

        return res.json();
    };

    const fetchOptions = useCallback(async (endpoint: string): Promise<OptionDto[]> => {
        const res = await fetch(`${apiUrl}${endpoint}`);
        return handleApiResponse<OptionDto[]>(res);
    }, []);

    const fetchBookings = useCallback(async (email: string) => {
        try {
            const res = await fetch(
                `${apiUrl}/api/FormMeeting?kontaktPersonEmail=${encodeURIComponent(email)}&eventId=${parsedEventId}`
            );;
            const data = await handleApiResponse<FormMeetingDto[]>(res);
            setBookings(data);
        } catch (error: any) {
            setBookings([]);
            setErrorMessage(error?.message || t.loadError);
        }
    }, [t.loadError]);

    useEffect(() => {
        const loadInitialData = async () => {
            try {
                const [pakker, bordopstillinger, udstyr] = await Promise.all([
                    fetchOptions(`/api/Option_MeetingPakke`),
                    fetchOptions(`/api/Option_MeetingBordopstilling`),
                    fetchOptions(`/api/Option_MeetingUdstyr`),
                ]);

                setMeetingPakker(pakker);
                setMeetingBordopstillinger(bordopstillinger);
                setMeetingUdstyrOptions(udstyr);
            } catch (error: any) {
                setErrorMessage(error?.message || t.loadError);
            }
        };

        loadInitialData();
    }, [fetchOptions, t.loadError]);

    useEffect(() => {
        const email = formData.kontaktPersonEmail?.trim();

        if (isValidEmail(email)) {
            fetchBookings(email);
        } else {
            setBookings([]);
        }
    }, [formData.kontaktPersonEmail, fetchBookings]);

    const getOptionName = (options: OptionDto[], value?: number) =>
        options.find((p) => p.value === value)?.text || t.dash;

    const formatDateForInput = (value?: string) => {
        if (!value) return "";
        return value.includes("T") ? value.split("T")[0] : value;
    };

    const formatTimeForInput = (value?: string) => {
        if (!value) return "";
        return value.split(":").slice(0, 2).join(":");
    };

    const formatTimeForBackend = (value?: string) => {
        if (!value) return value;
        const parts = value.split(":");
        if (parts.length === 3) return value;
        if (parts.length === 2) return `${parts[0]}:${parts[1]}:00`;
        return value;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setSavedMessage("");
        setErrorMessage("");
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const resetForm = () => {
        setFormData({});
        setEditingId(null);
        setActive(0);
        setSavedMessage("");
        setErrorMessage("");
        setBookings([]);
    };

    const handleEdit = (booking: FormMeetingDto) => {
        setEditingId(booking.meetingId ?? null);
        setSavedMessage("");
        setErrorMessage("");
        setFormData({
            ...booking,
            meetingDate: formatDateForInput(booking.meetingDate),
            meetingStart: formatTimeForInput(booking.meetingStart),
            meetingSlut: formatTimeForInput(booking.meetingSlut),
        });
        setActive(0);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const isStepValid = (step: number) => {
        switch (step) {
            case 0:
                return isValidEmail(formData.kontaktPersonEmail);
            case 1:
                return (
                    !!formData.meetingName &&
                    !!formData.meetingDate &&
                    !!formData.meetingStart &&
                    !!formData.meetingSlut &&
                    !!formData.meetingPakkeId &&
                    !!formData.antalPersoner
                );
            case 2:
                return (
                    !!formData.bordOpstilling &&
                    (formData.udstyr?.length ?? 0) > 0
                );
            default:
                return true;
        }
    };

    const nextStep = () => {
        if (!isStepValid(active)) {
            setErrorMessage(active === 0 ? t.invalidEmail : t.validationError);
            return;
        }
        setErrorMessage("");
        setActive((prev) => prev + 1);
    };

    const prevStep = () => setActive((prev) => prev - 1);

    const handleSubmit = async () => {
        if (!isStepValid(0)) {
            setErrorMessage(t.invalidEmail);
            setActive(0);
            return;
        }
        if (!isStepValid(1)) {
            setErrorMessage(t.validationError);
            setActive(1);
            return;
        }
        if (!isStepValid(2)) {
            setErrorMessage(t.validationError);
            setActive(2);
            return;
        }

        setIsSubmitting(true);
        setErrorMessage("");
        setSavedMessage("");

        try {
            const isEditing = !!editingId;
            const email = formData.kontaktPersonEmail ?? "";
            const url = isEditing
                ? `${apiUrl}/api/FormMeeting/${editingId}?kontaktPersonEmail=${encodeURIComponent(email)}`
                : `${apiUrl}/api/FormMeeting`;

            const method = isEditing ? "PUT" : "POST";

            const payload = {
                meetingId: editingId ?? 0,
                meetingDate: formData.meetingDate,
                meetingStart: formatTimeForBackend(formData.meetingStart),
                meetingSlut: formatTimeForBackend(formData.meetingSlut),
                meetingName: formData.meetingName,
                kontaktPerson: formData.kontaktPerson,
                kontaktPersonMobile: formData.kontaktPersonMobile,
                kontaktPersonEmail: formData.kontaktPersonEmail,
                antalPersoner: formData.antalPersoner ?? 0,
                bordOpstilling: formData.bordOpstilling ?? 0,
                udstyr: formData.udstyr ?? [],
                meetingPakkeId: formData.meetingPakkeId ?? 0,
                eventId: parsedEventId,
                createdByUserId: parsedUserId,
            };

            const res = await fetch(url, {
                method,
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            await handleApiResponse<any>(res);

            if (email) {
                await fetchBookings(email);
            }

            setSavedMessage(isEditing ? t.savedUpdate : t.savedCreate);
            setActive(4);
        } catch (err: any) {
            setErrorMessage(err?.message || t.saveError);
        } finally {
            setIsSubmitting(false);
        }
    };

    const antalOptions = useMemo(
        () =>
            Array.from({ length: 60 }, (_, i) => ({
                value: String(i + 1),
                label: String(i + 1),
            })),
        []
    );

    const toggleUdstyr = (id: number) => {
        setFormData((prev) => {
            const current = prev.udstyr ?? [];

            return {
                ...prev,
                udstyr: current.includes(id)
                    ? current.filter(x => x !== id)
                    : [...current, id],
            };
        });
    };


    if (!parsedEventId) {
        return <div>Manglende eller ugyldigt eventId</div>;
    }

    return (
        <div>
            <HeroSectionV2
                titleEn={t.heroTitleEn}
                titleDa={t.heroTitleDa}
                descriptionEn={t.heroDescEn}
                descriptionDa={t.heroDescDa}
                imageSrc={Billede1}
            />

            <div className="group-card" style={{ maxWidth: 900, margin: "40px auto" }}>
                <button className="group-card-button">
                    {editingId ? t.cardTitleEdit : t.cardTitleCreate}
                </button>

                <div className="group-card-content">
                    {errorMessage && (
                        <p style={{ color: "red", marginBottom: 12 }}>{errorMessage}</p>
                    )}
                    {savedMessage && active < 4 && (
                        <p style={{ color: "green", marginBottom: 12 }}>{savedMessage}</p>
                    )}

                    <Stepper
                        active={active}
                        onStepClick={setActive}
                        allowNextStepsSelect={false}
                        color="#e63946"
                        styles={{
                            stepIcon: {
                                borderRadius: "50%",
                                borderColor: "#e63946",
                                color: "#e63946",
                                width: 36,
                                height: 36,
                            },
                            stepCompletedIcon: {
                                borderRadius: "50%",
                                backgroundColor: "#e63946",
                                borderColor: "#e63946",
                                width: 36,
                                height: 36,
                            },
                            separator: {
                                borderColor: "#e63946",
                            },
                        }}
                    >
                        <Stepper.Step label={t.stepContact}>
                            <label>{t.lblEmail}</label>
                            <input
                                type="email"
                                name="kontaktPersonEmail"
                                value={formData.kontaktPersonEmail || ""}
                                onChange={handleChange}
                            />

                            <label>{t.lblContactPerson}</label>
                            <input
                                name="kontaktPerson"
                                value={formData.kontaktPerson || ""}
                                onChange={handleChange}
                            />

                            <label>{t.lblMobile}</label>
                            <input
                                name="kontaktPersonMobile"
                                value={formData.kontaktPersonMobile || ""}
                                onChange={handleChange}
                            />
                        </Stepper.Step>

                        <Stepper.Step label={t.stepBasic}>
                            <label>{t.lblMeetingName} *</label>
                            <input
                                name="meetingName"
                                value={formData.meetingName || ""}
                                onChange={handleChange}
                            />

                            <label>{t.lblMeetingDate} *</label>
                            <input
                                type="date"
                                name="meetingDate"
                                value={formData.meetingDate || ""}
                                onChange={handleChange}
                            />

                            <label>{t.lblMeetingStart} *</label>
                            <input
                                type="time"
                                name="meetingStart"
                                value={formData.meetingStart || ""}
                                onChange={handleChange}
                            />

                            <label>{t.lblMeetingSlut} *</label>
                            <input
                                type="time"
                                name="meetingSlut"
                                value={formData.meetingSlut || ""}
                                onChange={handleChange}
                            />

                            <label>{t.lblPackage} *</label>
                            <Select
                                value={formData.meetingPakkeId ? String(formData.meetingPakkeId) : null}
                                onChange={(val) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        meetingPakkeId: val ? Number(val) : undefined,
                                    }))
                                }
                                data={meetingPakker.map((p) => ({
                                    value: String(p.value),
                                    label: p.text,
                                }))}
                            />

                            <label>{t.lblPersons} *</label>
                            <Select
                                value={formData.antalPersoner ? String(formData.antalPersoner) : null}
                                onChange={(val) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        antalPersoner: val ? Number(val) : undefined,
                                    }))
                                }
                                data={antalOptions}
                            />
                        </Stepper.Step>

                        <Stepper.Step label={t.stepSetup}>
                            <label>{t.lblTableSetup} *</label>
                            <Select
                                value={formData.bordOpstilling ? String(formData.bordOpstilling) : null}
                                onChange={(val) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        bordOpstilling: val ? Number(val) : undefined,
                                    }))
                                }
                                data={meetingBordopstillinger.map((p) => ({
                                    value: String(p.value),
                                    label: p.text,
                                }))}
                            />

                            <label>{t.lblEquipment} *</label>

                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
                                    gap: 12,
                                    marginTop: 10,
                                }}
                            >
                                {meetingUdstyrOptions.map((item) => {
                                    const selected = formData.udstyr?.includes(item.value) ?? false;

                                    return (
                                        <label
                                            key={item.value}
                                            style={{
                                                position: "relative",
                                                cursor: "pointer",
                                            }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selected}
                                                onChange={() => toggleUdstyr(item.value)}
                                                style={{
                                                    position: "absolute",
                                                    opacity: 0,
                                                    pointerEvents: "none",
                                                }}
                                            />

                                            <div
                                                style={{
                                                    padding: "14px 16px",
                                                    borderRadius: 12,
                                                    border: selected
                                                        ? "2px solid #e63946"
                                                        : "1px solid #ddd",
                                                    backgroundColor: selected
                                                        ? "rgba(230,57,70,0.08)"
                                                        : "transparent",
                                                    transition: "all 0.2s ease",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 10,
                                                    minHeight: 50,
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        width: 20,
                                                        height: 20,
                                                        borderRadius: 6,
                                                        border: selected
                                                            ? "2px solid #e63946"
                                                            : "2px solid #aaa",
                                                        backgroundColor: selected
                                                            ? "#e63946"
                                                            : "transparent",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        color: "white",
                                                        fontSize: 13,
                                                        fontWeight: "bold",
                                                    }}
                                                >
                                                    {selected ? "✓" : ""}
                                                </div>

                                                <span
                                                    style={{
                                                        fontWeight: selected ? 600 : 400,
                                                    }}
                                                >
                                                    {item.text}
                                                </span>
                                            </div>
                                        </label>
                                    );
                                })}
                            </div>
                        </Stepper.Step>

                        <Stepper.Step label={t.stepConfirm}>
                            <p><b>{t.lblContactPerson}:</b> {formData.kontaktPerson || t.dash}</p>
                            <p><b>{t.lblMobile}:</b> {formData.kontaktPersonMobile || t.dash}</p>
                            <p><b>{t.lblEmail}:</b> {formData.kontaktPersonEmail || t.dash}</p>

                            <hr />

                            <p><b>{t.lblMeetingName}:</b> {formData.meetingName || t.dash}</p>
                            <p><b>{t.lblMeetingDate}:</b> {formData.meetingDate || t.dash}</p>
                            <p><b>{t.lblMeetingStart}:</b> {formData.meetingStart || t.dash}</p>
                            <p><b>{t.lblMeetingSlut}:</b> {formData.meetingSlut || t.dash}</p>
                            <p><b>{t.lblPackage}:</b> {getOptionName(meetingPakker, formData.meetingPakkeId)}</p>
                            <p><b>{t.lblPersons}:</b> {formData.antalPersoner ?? t.dash}</p>

                            <hr />

                            <p><b>{t.lblTableSetup}:</b> {getOptionName(meetingBordopstillinger, formData.bordOpstilling)}</p>
                            <p>
                                <b>{t.lblEquipment}:</b>{" "}
                                {formData.udstyr && formData.udstyr.length > 0
                                    ? formData.udstyr
                                        .map(id => getOptionName(meetingUdstyrOptions, id))
                                        .join(", ")
                                    : t.dash}
                            </p>
                        </Stepper.Step>

                        <Stepper.Completed>
                            <p>{savedMessage || (editingId ? t.savedUpdate : t.savedCreate)}</p>
                            <button style={{ marginTop: 10 }} onClick={resetForm}>
                                {t.newBooking}
                            </button>
                        </Stepper.Completed>
                    </Stepper>

                    {active < 4 && (
                        <div style={{ marginTop: 20 }}>
                            {active > 0 && <button onClick={prevStep}>{t.back}</button>}

                            {active === 3 ? (
                                <button
                                    style={{ marginLeft: 10 }}
                                    onClick={handleSubmit}
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? "..." : editingId ? t.save : t.create}
                                </button>
                            ) : (
                                <button
                                    style={{ marginLeft: 10 }}
                                    onClick={nextStep}
                                    disabled={!isStepValid(active)}
                                >
                                    {t.next}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <div className="group-card" style={{ maxWidth: 900, margin: "0 auto 40px" }}>
                <button className="group-card-button">{t.tableTitle}</button>

                <div className="group-card-content">
                    {!formData.kontaktPersonEmail || bookings.length === 0 ? (
                        <p>{t.noBookings}</p>
                    ) : (
                        <div style={{ overflowX: "auto" }}>
                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                <thead>
                                    <tr>
                                        <th style={{ textAlign: "left", padding: "10px", borderBottom: "1px solid #ddd" }}>{t.tableName}</th>
                                        <th style={{ textAlign: "left", padding: "10px", borderBottom: "1px solid #ddd" }}>{t.tableDate}</th>
                                        <th style={{ textAlign: "left", padding: "10px", borderBottom: "1px solid #ddd" }}>{t.tableStart}</th>
                                        <th style={{ textAlign: "left", padding: "10px", borderBottom: "1px solid #ddd" }}>{t.tableEnd}</th>
                                        <th style={{ textAlign: "left", padding: "10px", borderBottom: "1px solid #ddd" }}>{t.tablePackage}</th>
                                        <th style={{ textAlign: "left", padding: "10px", borderBottom: "1px solid #ddd" }}>{t.tablePersons}</th>
                                        <th style={{ textAlign: "left", padding: "10px", borderBottom: "1px solid #ddd" }}>{t.tableAction}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {bookings.map((booking) => (
                                        <tr key={booking.meetingId}>
                                            <td style={{ padding: "10px", borderBottom: "1px solid #eee" }}>
                                                {booking.meetingName || t.dash}
                                            </td>
                                            <td style={{ padding: "10px", borderBottom: "1px solid #eee" }}>
                                                {formatDateForInput(booking.meetingDate) || t.dash}
                                            </td>
                                            <td style={{ padding: "10px", borderBottom: "1px solid #eee" }}>
                                                {formatTimeForInput(booking.meetingStart) || t.dash}
                                            </td>
                                            <td style={{ padding: "10px", borderBottom: "1px solid #eee" }}>
                                                {formatTimeForInput(booking.meetingSlut) || t.dash}
                                            </td>
                                            <td style={{ padding: "10px", borderBottom: "1px solid #eee" }}>
                                                {getOptionName(meetingPakker, booking.meetingPakkeId)}
                                            </td>
                                            <td style={{ padding: "10px", borderBottom: "1px solid #eee" }}>
                                                {booking.antalPersoner ?? t.dash}
                                            </td>
                                            <td style={{ padding: "10px", borderBottom: "1px solid #eee" }}>
                                                <button onClick={() => handleEdit(booking)}>
                                                    {t.edit}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CreateMeeting;